import { useState } from 'react'
import Menu from '@mui/material/Menu'
import styled from 'styled-components'
import MenuItem from '@mui/material/MenuItem'
import { useTranslation } from 'react-i18next'
import {
  SmallDownload,
  SmallCopy,
  VerticalMenu,
  FavouriteHeart,
  ContractTransparent
} from '@/components/icons'
import ENV_VARS from '@/config'
import { showCopyApplicationModal } from '@/components/copy-application-modal'
import { colors, breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { show as MobileMenuModal } from '@/pages/Application/ApplicationViewV2/modal'
const { BACKEND_SERVER } = ENV_VARS

const ButtonWrapper = styled.div`
  cursor: pointer;
`

const ItemContainer = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: ${colors.white};

  :hover {
    opacity: 0.7;
  }

  ${props => props.isDesktop && 'padding: 15px; height: auto;'}
`

const ClickWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  pointer-events: none;
`

const IconWrapper = styled.div`
  margin-right: 10px;
`

const ItemText = styled.span`
  font-size: 14px;
  color: ${({ isDisabled }) => (isDisabled ? colors.grey64 : colors.grey24)};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 16px;
  }

  button,
  a {
    font-size: 16px;
    font-weight: 400;
    color: ${colors.grey24} !important;
    background-color: white !important;
    box-shadow: none !important;
    border: 0 !important;
    padding: 0;
  }
`

const ChatMenuWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  padding: 10px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  background-color: ${colors.white};
  border: 1.5px solid ${colors.grey88};
  margin-left: 10px;
  pointer-events: none;
`

const HeartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${colors.white};
  opacity: 0.6;
  border-radius: 50%;
  padding-right: 8px;
  position: relative;
  top: unset;
  right: unset;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 1;
  }
  > svg {
    pointer-events: none;
    > path {
      transition: fill 0.2s ease-in-out;
      stroke-width: 2;
      fill: ${props => (props.filled ? colors.red56 : 'rgba(0,0,0,0)')};
    }
  }
`

const PdfLink = styled.a`
  text-decoration: none;
  width: 100%;
`

export const ApplicationMenuItems = ({
  handleAddFavourites,
  isFavourite,
  canCopyApplication,
  handleCopyApplicant,
  handleClose,
  t,
  isDesktop,
  getContractButton,
  history,
  application
}) => {
  const applicationListingId = application.listing_id
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language
  const isNotEn = currentLanguage === 'zh'
  const pdfUrl = `${BACKEND_SERVER}/applications/${application.id}/application.pdf${
    isNotEn ? '?locale=zh-CN' : ''
  }`
  return (
    <>
      {!isDesktop && (
        <MenuItem>
          <ItemContainer
            id="application-trust-score-sidebar-menu-create-contract"
            isDesktop={isDesktop}
            onClick={() => {
              history.push(`/listing/${applicationListingId}/contract/choose/types`)
              handleClose()
            }}
          >
            <ClickWrapper>
              <IconWrapper>
                <ContractTransparent fill={colors.grey56} />
              </IconWrapper>
              <ItemText>{getContractButton()}</ItemText>
            </ClickWrapper>
          </ItemContainer>
        </MenuItem>
      )}
      <MenuItem>
        <ItemContainer
          id="application-trust-score-sidebar-menu-add-fav"
          isDesktop={isDesktop}
          onClick={handleAddFavourites}
        >
          <ClickWrapper>
            <HeartWrapper filled={isFavourite}>
              <FavouriteHeart />
            </HeartWrapper>
            <ItemText>
              {isFavourite
                ? t('b.chat.view.remove_from_favourites.button', 'Remove from favourites')
                : t('b.chat.view.favourites.button_add', 'Add to favourites')}
            </ItemText>
          </ClickWrapper>
        </ItemContainer>
      </MenuItem>

      <MenuItem sx={{ width: '100%' }}>
        <PdfLink href={pdfUrl} target="_blank">
          <ItemContainer
            onClick={e => {
              if (isMobile) {
                e.stopPropagation()
              }
            }}
            isDesktop={isDesktop}
            id="application-trust-score-sidebar-menu-export-pdf"
          >
            <ClickWrapper>
              <IconWrapper>
                <SmallDownload />
              </IconWrapper>
              <ItemText>{t('l.application.view.export_pdf.text', 'Export to PDF')} </ItemText>
            </ClickWrapper>
          </ItemContainer>
        </PdfLink>
      </MenuItem>

      {canCopyApplication && (
        <MenuItem>
          <ItemContainer
            id="application-trust-score-sidebar-menu-copy"
            isDesktop={isDesktop}
            onClick={() => {
              handleCopyApplicant()
              handleClose()
            }}
          >
            <ClickWrapper>
              <IconWrapper>
                <SmallCopy />
              </IconWrapper>
              <ItemText>
                {t('l.application.view.copy_to_listing.text', 'Copy to another listing')}
              </ItemText>
            </ClickWrapper>
          </ItemContainer>
        </MenuItem>
      )}
    </>
  )
}

const ApplicationDropdownMenu = ({
  isFavourite,
  handleFavourite,
  handleCopyApplicant,
  getContractButton,
  history,
  application
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const menuId = open ? 'simple-popover' : undefined
  const canCopyApplication = !!handleCopyApplicant
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)

  const handleClick = e => {
    isMobile
      ? MobileMenuModal({
          application,
          history,
          canCopyApplication,
          showCopyApplicationModal,
          getContractButton,
          preferredName: application?.user_info?.preferred_name
        })
      : open
      ? handleClose()
      : setAnchorEl(e.currentTarget)
  }
  const handleClose = e => {
    setAnchorEl(null)
  }

  const handleAddFavourites = () => {
    handleFavourite()
    handleClose()
  }

  const { t } = useTranslation()

  return (
    <>
      <ButtonWrapper onClick={handleClick} id="application-trust-score-sidebar-menu">
        <ChatMenuWrapper>
          <VerticalMenu />
        </ChatMenuWrapper>
      </ButtonWrapper>
      <Menu
        id={menuId}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            border: `1.5px solid ${colors.grey88}`,
            boxShadow:
              '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
            width: 222,
            borderRadius: '6px',
            marginTop: '3px'
          },
          '& .MuiMenuItem-root': {
            padding: 0
          },
          '& .MuiList-root': {
            padding: 0
          }
        }}
      >
        <ApplicationMenuItems
          isFavourite={isFavourite}
          canCopyApplication={canCopyApplication}
          handleCopyApplicant={handleCopyApplicant}
          handleClose={handleClose}
          handleAddFavourites={handleAddFavourites}
          t={t}
          isDesktop={true}
          getContractButton={getContractButton}
          history={history}
          application={application}
        />
      </Menu>
    </>
  )
}

export default ApplicationDropdownMenu
